import React from 'react';
import Block from './Block';
import PillTag from './PillTag';

/**
 * Renders a `target_suggestions` block from the backend's TargetSuggestions
 * payload: { target_suggestions: [{column, task, reasoning, relevance_score, caveats}] }.
 *
 * Clicking a row calls `onSelectTarget(column)` which the page wires to the
 * agent — the user's selection is sent as a chat message that triggers
 * preliminary-model training for that column.
 */
const MAX_LIST_HEIGHT = 320;

const TargetSuggestions = ({ blockData = {}, onSelectTarget }) => {
  const items = blockData.target_suggestions || [];
  return (
    <Block
      icon="target"
      title="Suggested targets"
      badge={<PillTag tone="accent">{items.length}</PillTag>}
      footer={onSelectTarget ? (
        <span style={{ color: 'var(--fg-subtle)' }}>
          Click a target to train a preliminary model.
        </span>
      ) : null}
    >
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 8,
        maxHeight: MAX_LIST_HEIGHT, overflowY: 'auto',
        margin: -4, padding: 4,
      }}>
        {items.map((s, i) => {
          const score = Math.max(0, Math.min(1, Number(s.relevance_score || 0)));
          const clickable = !!onSelectTarget;
          return (
            <button
              type="button"
              key={`${s.column}-${i}`}
              onClick={clickable ? () => onSelectTarget(s.column) : undefined}
              disabled={!clickable}
              style={{
                textAlign: 'left',
                padding: '10px 12px',
                background: 'color-mix(in srgb, var(--fg) 4%, transparent)',
                border: '1px solid var(--line-soft)',
                borderRadius: 'var(--r-md)',
                display: 'flex', flexDirection: 'column', gap: 5,
                cursor: clickable ? 'pointer' : 'default',
                font: 'inherit', color: 'inherit',
                transition: 'border-color 120ms ease, background 120ms ease',
              }}
              onMouseEnter={(e) => {
                if (!clickable) return;
                e.currentTarget.style.borderColor = 'var(--accent-soft-2)';
                e.currentTarget.style.background = 'var(--muted)';
              }}
              onMouseLeave={(e) => {
                if (!clickable) return;
                e.currentTarget.style.borderColor = 'var(--line-soft)';
                e.currentTarget.style.background = 'color-mix(in srgb, var(--fg) 4%, transparent)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--foreground)' }}>
                  {s.column}
                </span>
                {s.prediction_mode === 'forecast' && (
                  <PillTag tone="accent">Forecast</PillTag>
                )}
                <div style={{ flex: 1 }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--muted-foreground)', fontVariantNumeric: 'tabular-nums' }}>
                  <div style={{ width: 60, height: 4, background: 'var(--muted)', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: `${score * 100}%`, height: '100%', background: 'var(--chart-1)' }} />
                  </div>
                  {(score * 100).toFixed(0)}%
                </div>
              </div>
              {s.reasoning && (
                <div style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.45 }}>
                  {s.reasoning}
                </div>
              )}
              {/* Caveat pills hidden for now — may bring back later. */}
            </button>
          );
        })}
      </div>
    </Block>
  );
};

export default TargetSuggestions;
